import { use, useEffect, useMemo, useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ChatContext } from '@app/context'
import { getConversationById } from '@app/helpers'
import {
  ChatAction,
  ConversationsListStatus,
  useConversationID,
  useIsStaff,
} from '@app/hooks'
import { trackChatFilterSelected } from '@app/services/tracking'
import { isB2BView, toTitleCase } from '@app/utils'

import { extractConversationFilters, shouldKeepConversation } from './helpers'
import styles from './styles.module.scss'

export const Header = () => {
  const { t } = useTranslation()
  const { state, dispatch } = use(ChatContext)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isStaff = useIsStaff()
  const conversationId = useConversationID()
  const isB2B = isB2BView()
  const canShowSpamFilter = state.hasSpam || isStaff
  const shouldShowListFilters = isB2B || canShowSpamFilter

  const updateMessages = useCallback(
    (
      updateParams: (params: URLSearchParams) => void,
      ...dispatchActions: ChatAction[]
    ) => {
      const newParams = new URLSearchParams(searchParams.toString())
      updateParams(newParams)
      const filters = extractConversationFilters(newParams)
      const conversation = getConversationById(
        state.conversations,
        conversationId ?? undefined
      )
      const conversationExists = shouldKeepConversation(
        conversation ?? undefined,
        filters
      )
      const pathname = conversationExists && conversation
        ? `/messages/${conversation.id}`
        : '/messages/'
      const searchString = newParams.toString()
      navigate({
        pathname,
        search: searchString ? `?${searchString}` : '',
      })
      dispatchActions.forEach((action) => dispatch(action))
    },
    [
      navigate,
      searchParams,
      dispatch,
      conversationId,
      state.conversations,
    ]
  )

  const setConversationsListStatus = useCallback(
    (status: ConversationsListStatus) =>
      updateMessages(
        (params) => {
          if (status === 'dispatched') {
            params.delete('status')
          } else {
            params.set('status', status)
          }
          params.delete('replied')
        },
        { type: 'SET_REPLY_DETECTION_STATUS', payload: undefined },
        { type: 'SET_CONVERSATIONS_LIST_STATUS', payload: status }
      ),
    [updateMessages]
  )

  const setRepliedFilter = useCallback(
    () => {
      const isActive = state.replyDetectionStatus === 'Replied'

      if (isActive) {
        return
      }

      updateMessages(
        (params) => {
          params.delete('status')
          params.set('replied', 'true')
        },
        { type: 'SET_REPLY_DETECTION_STATUS', payload: 'Replied' },
        { type: 'SET_CONVERSATIONS_LIST_STATUS', payload: 'dispatched' }
      )

      trackChatFilterSelected('Replied')
    },
    [state.replyDetectionStatus, updateMessages]
  )

  const setUnrepliedFilter = useCallback(
    () => {
      const isActive =
        state.status === 'dispatched' &&
        state.replyDetectionStatus === 'Unreplied'

      if (isActive) {
        return
      }

      updateMessages(
        (params) => {
          params.delete('status')
          params.set('replied', 'false')
        },
        { type: 'SET_REPLY_DETECTION_STATUS', payload: 'Unreplied' },
        { type: 'SET_CONVERSATIONS_LIST_STATUS', payload: 'dispatched' }
      )

      trackChatFilterSelected('New conversations')
    },
    [state.replyDetectionStatus, state.status, updateMessages]
  )

  const handleShowDispatched = useCallback(
    () => {
      const isActive =
        state.status === 'dispatched' && !state.replyDetectionStatus

      if (isActive) {
        return
      }

      setConversationsListStatus('dispatched')

      trackChatFilterSelected('All')
    },
    [setConversationsListStatus, state.replyDetectionStatus, state.status]
  )

  const handleShowSpam = useCallback(
    () => {
      const isActive = state.status === 'spam'

      if (isActive) {
        return
      }

      setConversationsListStatus('spam')

      trackChatFilterSelected('Spam')
    },
    [setConversationsListStatus, state.status]
  )

  useEffect(() => {
    if (
      state.status === 'spam' &&
      state.fetching === 'SUCCEED' &&
      !state.hasSpam
    ) {
      setConversationsListStatus('dispatched')
    }
  }, [state.status, state.fetching, state.hasSpam, setConversationsListStatus])

  const dispatchedStatusClassName = useMemo(
    () =>
      state.status === 'dispatched' && !state.replyDetectionStatus
        ? styles.selected
        : undefined,
    [state.status, state.replyDetectionStatus]
  )

  const unrepliedStatusClassName = useMemo(
    () =>
      state.status === 'dispatched' && state.replyDetectionStatus === 'Unreplied'
        ? styles.selected
        : undefined,
    [state.status, state.replyDetectionStatus]
  )

  const spamStatusClassName = useMemo(
    () => (state.status === 'spam' ? styles.selected : undefined),
    [state.status]
  )

  const repliedStatusClassName = useMemo(
    () =>
      state.replyDetectionStatus === 'Replied' ? styles.selected : undefined,
    [state.replyDetectionStatus]
  )

  return (
    <div className={styles.top}>
      <h1>{t('Chat.Title')}</h1>

      {shouldShowListFilters && (
        <div className={styles.buttons}>
          <button
            onClick={handleShowDispatched}
            className={dispatchedStatusClassName}
          >
            {toTitleCase(t('Chat.All'))}
          </button>
          {!isStaff && isB2B && (
            <button
              onClick={setUnrepliedFilter}
              className={unrepliedStatusClassName}
            >
              {toTitleCase(t('Chat.NewConversations'))}
            </button>
          )}
          {isStaff && isB2B && (
            <button
              onClick={setRepliedFilter}
              className={repliedStatusClassName}
            >
              {toTitleCase(t('Chat.Replied'))}
            </button>
          )}
          {canShowSpamFilter && (
            <button
              onClick={handleShowSpam}
              className={spamStatusClassName}
            >
              {toTitleCase(t('Chat.Spam'))}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
